/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Ember from 'ember';
import Component from '@glimmer/component';
import { timeout } from 'ember-concurrency';
import { restartableTask } from 'ember-concurrency';
import { inject as service } from '@ember/service';
import { action, computed } from '@ember/object';
import RouterService from '@ember/routing/router-service';
import { tracked } from '@glimmer/tracking';
import ApiService from 'cio/services/api';
import { IntegrationStatus, IntegrationMethod } from 'cio/models/setup-guide';
import config from 'cio/config/environment';
import AnalyticsService from 'cio/services/analytics';
import { SetupGuideDataType } from 'cio/utils/analytics/transforms/onboarding';
import EnvironmentService from 'cio/services/environment';
import AuthService from 'cio/services/auth';
import { FlyMouseEvent } from '@customerio/fly-components/types/events';
import { announceHydraNavigate } from '@customerio/hydra-nav-state';
import { ProductApp } from 'cio/utils/product-app-info';
import AccountService from 'cio/services/account';
import SetupGuideSourcesApi from 'cio/components/setup-guide/sources/api';
import SetupGuideSourcesDataPipelines from 'cio/components/setup-guide/sources/data-pipelines';
import SetupGuideSourcesSegment from 'cio/components/setup-guide/sources/segment';
import SetupGuideSourcesSnippet from 'cio/components/setup-guide/sources/snippet';
import SetupGuideSourcesSqlImport from 'cio/components/setup-guide/sources/sql-import';
import SetupGuideSourcesForms from 'cio/components/setup-guide/sources/forms';
import { ComponentLike } from '@glint/template';

const REQUIRED_CUSTOMERS = 1;
const REQUIRED_ATTRIBUTES = 1;
const REQUIRED_EVENTS = 1;

type IntegrationMethodDetails = {
  id: IntegrationMethod;
  icon?: string;
  iconUrl?: string;
  preTitle?: string;
  title: string;
  description: string;
  component?: ComponentLike<any>;
  colSpanClass: string;
  disabled?: boolean;
  disableForEUDataCenter?: boolean;
  disabledTooltipContent?: string;
};

interface SetupGuideSourcesArgs {
  integrationMethod: IntegrationMethod;
  integrationStatus: IntegrationStatus;
  onSelect: (method: IntegrationMethod) => unknown;
  onComplete: () => unknown;
}

const API_METHOD: IntegrationMethodDetails = {
  id: 'api',
  icon: 'cio-logo',
  preTitle: 'Integrate using our',
  title: 'API',
  description: 'The most flexible, secure, and performant way to integrate your data.',
  component: SetupGuideSourcesApi,
  colSpanClass: 'col-span-3',
};

const DATA_PIPELINES_METHOD: IntegrationMethodDetails = {
  id: 'data-pipelines',
  iconUrl: 'https://unityassets.customer.io/images/customerio-data-pipelines-b2474de2ebc57fb2d548cd9be8c1858d.svg',
  title: 'Data Pipelines',
  description:
    'Send data to Journeys from where data is created or stored – like your app, website, warehouse, and more. Plus, you can easily send data to the other analytics, sales, or marketing tools your team uses.',
  component: SetupGuideSourcesDataPipelines,
  colSpanClass: 'col-span-3',
};

const SEGMENT_METHOD: IntegrationMethodDetails = {
  id: 'segment',
  icon: 'segment-com',
  preTitle: 'Integrate with',
  title: 'Segment.com',
  description: 'Perfect for existing Segment users, simply add Customer.io as a destination.',
  component: SetupGuideSourcesSegment,
  colSpanClass: 'col-span-3',
};

const SNIPPET_METHOD: IntegrationMethodDetails = {
  id: 'snippet',
  icon: 'javascript',
  preTitle: 'Integrate using our',
  title: 'JavaScript Snippet',
  description: 'A quick way to get started, but not as powerful and robust as the API.',
  component: SetupGuideSourcesSnippet,
  colSpanClass: 'col-span-3',
};

const SQL_IMPORT_METHOD: IntegrationMethodDetails = {
  id: 'sql-import',
  icon: 'database',
  title: 'Databases and Data Warehouses',
  description: 'Connect your MySQL or PostgreSQL database to automatically import people.',
  component: SetupGuideSourcesSqlImport,
  colSpanClass: 'col-span-3',
};

const FORMS_METHOD: IntegrationMethodDetails = {
  id: 'forms',
  icon: 'form',
  title: 'Forms',
  description: 'Connect existing forms through Facebook, a JS snippet, or our form endpoint.',
  component: SetupGuideSourcesForms,
  colSpanClass: 'col-span-3',
};

const IMPORT_CSV_METHOD: IntegrationMethodDetails = {
  id: 'import-csv',
  icon: 'import',
  preTitle: 'Importing a',
  title: 'CSV',
  description:
    'Real-time data integrations are most powerful, but one-time imports are helpful to backfill historical data or augment your existing data. Learn more about importing People via CSV.',
  colSpanClass: 'col-span-3',
};

export default class SetupGuideSources extends Component<SetupGuideSourcesArgs> {
  @service account!: AccountService;
  @service auth!: AuthService;
  @service api!: ApiService;
  @service analytics!: AnalyticsService;
  @service router!: RouterService;
  @service environment!: EnvironmentService;

  get importCSVMethod() {
    return IMPORT_CSV_METHOD;
  }

  get shouldShowPipelinesAsOnlyIntegration() {
    return (this.account.current?.productActivations ?? []).includes(ProductApp.DATA_PIPELINES);
  }

  get pipelinesUrl() {
    return `/workspaces/${this.environment.current.id}/pipelines`;
  }

  get integrationMethods() {
    let methods = [API_METHOD, DATA_PIPELINES_METHOD, FORMS_METHOD, SEGMENT_METHOD, SQL_IMPORT_METHOD, SNIPPET_METHOD];

    if (this.environment.current.canIdentifyByEmail) {
      methods = [API_METHOD, DATA_PIPELINES_METHOD, SEGMENT_METHOD, SNIPPET_METHOD, SQL_IMPORT_METHOD, FORMS_METHOD];
    }

    return methods;
  }

  get allIntegrationsCardWidthClass() {
    return this.integrationMethods.length % 2 === 0 ? 'col-span-6' : 'col-span-3';
  }

  @computed('router.currentRouteName')
  get linkToStep() {
    let url = this.router.urlFor('setup-guide.sources');
    return `${window.location.origin}${url}`;
  }

  @computed('args.integrationMethod')
  get selectedMethod() {
    if (this.args.integrationMethod === 'import-csv') {
      return this.importCSVMethod;
    }

    return this.integrationMethods.findBy('id', this.args.integrationMethod);
  }

  get canSetupIntegration() {
    if (this.args.integrationMethod === 'import-csv') {
      return this.auth.hasPermission('journeys/imports/create');
    } else if (this.args.integrationMethod === 'api') {
      return this.auth.hasPermission('account/api_credentials/edit');
    } else if (this.args.integrationMethod === 'data-pipelines') {
      return this.auth.hasPermission('pipelines/destinations/create');
    } else if (this.args.integrationMethod === 'segment') {
      return this.auth.hasPermission('journeys/segment_integration/create');
    } else if (this.args.integrationMethod === 'forms') {
      return this.auth.hasPermission('journeys/forms/create');
    } else if (this.args.integrationMethod === 'sql-import') {
      return this.auth.hasPermission('journeys/sql_syncs/create');
    }

    return false;
  }

  @tracked events = 0;
  @tracked customers = 0;
  @tracked attributes = 0;

  zapierImage = new URL('cio/assets/images/integrations/zapier.svg', import.meta.url).toString();
  helpscoutImage = new URL('cio/assets/images/integrations/helpscout.svg', import.meta.url).toString();
  airtableImage = new URL('cio/assets/images/integrations/airtable.svg', import.meta.url).toString();

  pollForDataTask = restartableTask(async () => {
    // eslint-disable-next-line no-constant-condition
    while (true) {
      let { customers, attributes, events } = await this.api.request('setup_guide/integration_status');

      this.customers = customers;
      this.attributes = attributes;
      this.events = events;

      if (customers >= REQUIRED_CUSTOMERS && attributes >= REQUIRED_ATTRIBUTES && events >= REQUIRED_EVENTS) {
        if (this.args.integrationStatus !== 'complete') {
          this.args.onComplete();
        }

        break;
      }

      if (Ember.testing) {
        break;
      }

      await timeout(config.APP.pollingDelay);
    }
  });

  @action
  onDataTypeClick(type: SetupGuideDataType) {
    this.analytics.track('onboarding', 'data_type_clicked', { type });
  }

  @action
  onImportCsv() {
    this.analytics.track('onboarding', 'integration_details_clicked', { type: 'csv' });
  }

  @action
  onPipelinesLinkClick(event: FlyMouseEvent<HTMLAnchorElement>) {
    event.preventDefault();
    announceHydraNavigate(this.pipelinesUrl, { targetApp: ProductApp.DATA_PIPELINES });
  }
}
